<script lang="ts">
  import type { Auction, OffChainBid, PaymentMethod, User } from '$lib/interfaces';
  import { createEventDispatcher } from 'svelte';
  import { BITSKI_API_V1, MAX_CC_AMOUNT } from '$lib/constants';
  import { assert, expect, isError } from '$lib/helpers';
  import { STRIPE_API_KEY } from '$lib/env';
  import { formatCurrency } from '$lib/format';
  import { getStripe } from '$lib/stripe';
  import AddCardForm from '$lib/components/AddCardForm.svelte';
  import Auth from '$lib/components/Auth.svelte';
  import PaymentMethodPreview from '$lib/components/PaymentMethodPreview.svelte';
  import BidModal from './BidModal.svelte';
  import { size } from '$lib/responsive';
  import LottieSuccess from '$lib/components/lottie/LottieSuccess.svelte';
  import { page } from '$app/stores';
  import { fetchJson, getFirstAccount } from '$lib/api';

  interface BidPayload {
    bid: {
      auctionId: string;
      bidder: string;
      currency: 'USD';
      amount: string;
      recipient: string;
    };
    paymentMethodId: string;
  }

  interface BidRequest {
    payload: BidPayload;
    user: User;
    paymentMethod: PaymentMethod | null;
  }

  export let auction: Auction;
  export let currentBid: number;
  export let currency: string;
  export let isUrgent: boolean;
  export let isAvailable: boolean;
  export let isLiveAuction: boolean;
  export let isEndingAuction: boolean;
  export let isTestMode: boolean;

  const dispatch = createEventDispatcher<{ bidSuccess: OffChainBid }>();

  let isLoading = false;
  let newBid: BidRequest | null = null;
  let amount = '';
  let isSuccess = false;
  let error: string;
  let isAddingCard = false;
  let hasExistingCard = true;
  let parsedAmount: number;
  $: minBid = calculateMinBid(currentBid);

  function handleAmountChange(evt: Event & { currentTarget: HTMLInputElement }) {
    let [rawDollars, rawCents] = evt.currentTarget.value.split('.');
    let dollars = parseInt((rawDollars || '').replace(/\D/g, ''), 10) || 0;
    let cents =
      parseInt((rawCents || '').replace(/\D/g, '').padEnd(2, '0').substring(0, 2), 10) || 0;

    parsedAmount = Math.min(dollars + cents / 100, MAX_CC_AMOUNT);
    amount = formatCurrency(parsedAmount);

    // if a new bid already existed, we're creating another different bid,
    // generate a new bid to take its place
    if (newBid) {
      generateBid();
    }
  }

  function calculateMinBid(currentBid: number) {
    let calculatedMinBid = 0;
    if (currentBid >= 0 && currentBid <= 100) {
      calculatedMinBid = 5;
    } else if (currentBid >= 101 && currentBid <= 250) {
      calculatedMinBid = 10;
    } else if (currentBid >= 251 && currentBid <= 500) {
      calculatedMinBid = 25;
    } else if (currentBid >= 501 && currentBid <= 1000) {
      calculatedMinBid = 50;
    } else if (currentBid >= 1001 && currentBid <= 10000) {
      calculatedMinBid = 100;
    } else if (currentBid > 10000) {
      calculatedMinBid = 250;
    }
    return calculatedMinBid;
  }
  function validateAmount() {
    error = '';
    if (parsedAmount === undefined || parsedAmount < currentBid + minBid) {
      error = `Minimum bid was not reached! Please place a bid ${formatCurrency(
        currentBid + minBid,
        currency
      )}  or higher`;
      return false;
    }

    return true;
  }

  function handleBidSubmit() {
    if (newBid) {
      submitBid();
    } else {
      generateBid();
    }
  }

  async function generateBid() {
    if (isLoading) return;
    if (!validateAmount()) return;

    isLoading = true;

    const user = expect($page.data.user, 'expected a user to be signed in');
    const testMode = STRIPE_API_KEY.includes('test');

    try {
      const account = await getFirstAccount(fetch, expect($page.data.user, 'not logged in'));

      const paymentMethodsResponse = await fetch(
        `${BITSKI_API_V1}/users/${user.id}/payment-methods?test_mode=${testMode}`
      );
      const { paymentMethods } = (await paymentMethodsResponse.json()) as any;
      const firstPaymentMethod = paymentMethods[0];

      if (!firstPaymentMethod) {
        isAddingCard = true;
        hasExistingCard = false;
      }

      newBid = {
        payload: {
          bid: {
            auctionId: auction.id,
            currency: 'USD',
            bidder: user.id,
            amount: String(parsedAmount),
            recipient: account,
          },
          paymentMethodId: firstPaymentMethod?.id,
        },
        paymentMethod: firstPaymentMethod,
        user,
      };
    } catch (err) {
      console.error(err);
    } finally {
      isLoading = false;
    }
  }

  async function submitBid() {
    assert(newBid !== null, 'expected a new bid to exist already before submitting');
    if (!validateAmount()) return;

    isLoading = true;
    let userId = newBid.user.id;

    try {
      let json = await fetchJson(fetch, `${BITSKI_API_V1}/users/${userId}/bids`, {
        method: 'POST',
        body: newBid.payload,
      });

      if (json.payment && json.payment.nextAction) {
        const stripe = await getStripe(isTestMode);
        await stripe.confirmCardPayment(json.payment.processorPaymentSecret);

        json = await fetchJson(fetch, `${BITSKI_API_V1}/users/${userId}/bids/${json.bid.id}`, {
          method: 'PATCH',
          body: {
            payment: {},
          },
        });
      }

      if (json.bid) {
        amount = '';
        isSuccess = true;

        dispatch('bidSuccess', json.bid);
      } else {
        throw (json.error && json.error.message) || 'Something went wrong, try again.';
      }
    } catch (err) {
      error = isError(err) ? err.message : 'Something went wrong, try again.';
    } finally {
      isLoading = false;
    }
  }

  function refreshBidAndToggleAddCardForm() {
    generateBid();
    isAddingCard = false;
    hasExistingCard = true;
  }

  function cancelAddingCard() {
    isAddingCard = false;

    if (!hasExistingCard) {
      clearBid();
    }
  }

  function clearBid() {
    newBid = null;
    isSuccess = false;
  }
</script>

{#if isEndingAuction}
  <div class="toast toast-brand my-6">
    Auction close pending! We're verifying the bids for the auction! This will only take a minute or
    so.
  </div>
{:else if isAvailable}
  {#if $size.md}
    <div class="my-6">
      {#if auction.info && !auction.info.automaticSettlement}
        <div class="toast toast-blue mb-4">
          <span class="body-bold">ETH Settlement Accepted.</span>
          The auction winner will be contacted by Bitski and have 24 hours to manually settle. Accepted
          payment methods are ETH, wire, or ACH.
        </div>
      {/if}

      {#if $page.data.user}
        <form on:submit|preventDefault={handleBidSubmit}>
          <input
            class="input w-full mb-4"
            value={amount}
            disabled={isLoading}
            placeholder="Bid {formatCurrency(currentBid + minBid, currency)} or higher"
            on:change={handleAmountChange}
          />

          <button type="submit" disabled={isAddingCard} class="w-full btn btn-primary">
            {#if isSuccess}
              <LottieSuccess className="h-6" on:complete={clearBid} />
            {:else if isLoading}
              <i class="icon-spinner" />
            {:else if newBid}
              Confirm bid
            {:else}
              Place bid
            {/if}
          </button>
        </form>

        {#if isUrgent}
          <div class="toast toast-brand mt-4" class:relative={newBid}>
            If you place a bid now, the auction timer will reset to 5 minutes
          </div>
        {/if}

        {#if isAddingCard}
          {#if !hasExistingCard}
            <div class="toast toast-blue my-4">
              You don’t have a card added yet! Please fill in your card info below to continue
            </div>
          {/if}

          <AddCardForm
            showCancel
            className="mt-4"
            {isTestMode}
            on:add={refreshBidAndToggleAddCardForm}
            on:cancel={cancelAddingCard}
          />
        {:else if newBid}
          <PaymentMethodPreview
            brand={newBid.paymentMethod?.cardBrand || ''}
            description={newBid.paymentMethod?.id
              ? `${newBid.paymentMethod.cardBrand} • • • • ${newBid.paymentMethod.cardLast4}`
              : 'Adding payment method'}
            className="my-4"
            on:change={() => (isAddingCard = true)}
          />

          <div class="text-center caption-bold text-grey">
            {#if !auction.info?.automaticSettlement}
              A 10% hold will be put on your card when you place this bid. Your hold will be
              cancelled if you're outbid. If you place the winning bid, and fail to settle within
              24hrs, this hold will be forfeited.
            {:else}
              A hold will be put on your card when you place this bid. If you win, the full amount
              will be charged to your card.
            {/if}
          </div>
        {/if}

        {#if error}
          <div class="toast toast-brand mt-4">{error}</div>
        {/if}
      {:else}
        <Auth className="w-full btn btn-primary" loginText="Login to bid" />
      {/if}
    </div>
  {:else if isAddingCard}
    <BidModal title="Add card to bid" className="z-30" on:close={cancelAddingCard}>
      {#if !hasExistingCard}
        <div class="toast toast-blue hidden md:block mt-4">
          You don’t have a card added yet! Please fill in your card info below to continue
        </div>
      {/if}

      <AddCardForm
        showCancel
        {isTestMode}
        on:add={refreshBidAndToggleAddCardForm}
        on:cancel={cancelAddingCard}
      />
    </BidModal>
  {:else if newBid}
    <BidModal title="Confirm bid" className="z-20" on:close={clearBid}>
      <div class="text-center text-grey mb-6">
        {#if !auction.info?.automaticSettlement}
          A 10% hold will be put on your card when you place this bid. Your hold will be cancelled
          if you're outbid. If you place the winning bid, and fail to settle within 24hrs, this hold
          will be forfeited.
        {:else}
          A hold will be put on your card when you place this bid. If you win, the full amount will
          be charged to your card.
        {/if}
      </div>

      <PaymentMethodPreview
        brand={newBid.paymentMethod?.cardBrand || ''}
        description={newBid.paymentMethod?.id
          ? `${newBid.paymentMethod.cardBrand} • • • • ${newBid.paymentMethod.cardLast4}`
          : 'Adding payment method'}
        on:change={() => (isAddingCard = true)}
        className="mb-6"
      />

      <form on:submit|preventDefault={submitBid}>
        <button type="submit" class="w-full btn btn-primary">
          {#if isSuccess}
            <LottieSuccess className="h-8 -my-1" on:complete={() => (newBid = null)} />
          {:else if isLoading}
            <i class="icon-spinner" />
          {:else if newBid}
            Confirm {amount} bid
          {/if}
        </button>
      </form>

      {#if isUrgent}
        <div class="toast toast-brand hidden md:relative" class:relative={newBid}>
          If you place a bid now, the auction timer will reset to 5 minutes
        </div>
      {/if}
    </BidModal>
  {:else if $page.data.user}
    <form
      on:submit|preventDefault={generateBid}
      class="z-10 fixed px-6 py-4 shadow-lg border-t border-grey-aux w-full bg-white dark:bg-obsidian bottom-0 left-0"
    >
      <input
        class="input w-full"
        value={amount}
        disabled={isLoading}
        placeholder="Bid {formatCurrency(currentBid, currency)} or higher"
        on:change={handleAmountChange}
      />

      <button type="submit" class="w-full btn btn-primary mt-4">
        {#if isLoading}
          <i class="icon-spinner" />
        {:else}
          Place bid
        {/if}
      </button>

      {#if isUrgent}
        <div class="toast toast-brand hidden md:relative" class:relative={newBid}>
          If you place a bid now, the auction timer will reset to 5 minutes
        </div>
      {/if}

      {#if error}
        <div class="toast toast-brand mt-6">{error}</div>
      {/if}
    </form>
  {:else}
    <div
      class="fixed px-6 py-4 shadow-lg border-t border-grey-aux w-full bg-white dark:bg-obsidian bottom-0 left-0"
    >
      <Auth className="w-full btn btn-primary" loginText="Login to bid" />
    </div>
  {/if}
{:else}
  <button type="button" disabled class="btn btn-primary w-full my-6">
    {#if isLiveAuction}
      Sold
    {:else}
      Closed
    {/if}
  </button>
{/if}
